import { useTranslation } from "react-i18next";
import type { Theme } from "@mui/system";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Card,
    CardContent,
    Stack,
    useMediaQuery,
    useTheme,
} from "@vapor/react-material";
import type { ReactElement } from "react";
import { TaxSubmissionStatus } from "../../../../../core/dtos/TaxSubmissionStatus";
import { TaxSubmissionStatusAlert } from "../../../../components/tax-submission-status-alert/TaxSubmissionStatusAlert";
import { TaxSubmissionStatusChip } from "../../../../components/tax-submission-status-chip/TaxSubmissionStatusChip";

interface TaxSubmissionGenericErrorKPICardProps {
    isSelected: boolean;
    loading: boolean;
    numberOfTaxSubmissions: number;
    onClickKPI: (taxSubmissionStatus: TaxSubmissionStatus) => unknown;
}

export const TaxSubmissionGenericErrorKPICard = ({
    isSelected = false,
    loading,
    numberOfTaxSubmissions,
    onClickKPI,
}: TaxSubmissionGenericErrorKPICardProps): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down(theme.breakpoints.values.lg),
    );

    return (
        <Card
            elevation={isSelected ? 4 : 0}
            onClick={() =>
                !loading && onClickKPI(TaxSubmissionStatus.GENERIC_ERROR)
            }
            sx={{
                ":hover": {
                    boxShadow: 4,
                },
                border: `2px solid ${theme.palette.primary.accentRustyRed}`,
                cursor: "pointer",
                width: "100%",
                minHeight: "82px",
            }}
            style={{
                backgroundColor: isSelected
                    ? theme.palette.primary.whiteSmoke
                    : undefined,
            }}
        >
            <CardContent
                sx={{
                    paddingTop: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    paddingBottom: 1,
                    ":last-child": {
                        paddingBottom: 1,
                    },
                }}
            >
                <LoadingWrapper
                    loading={loading}
                    variant="backdrop"
                    skeletonProps={{ rowsNumber: 1 }}
                >
                    <Stack>
                        <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="start"
                            spacing={isSmallScreen ? 0 : 1}
                        >
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                variant="titleMedium"
                            >
                                {numberOfTaxSubmissions}
                            </Typography>

                            <TaxSubmissionStatusChip
                                plural
                                status={TaxSubmissionStatus.GENERIC_ERROR}
                            />
                            {!isSmallScreen && (
                                <TaxSubmissionStatusAlert
                                    status={TaxSubmissionStatus.GENERIC_ERROR}
                                />
                            )}
                        </Stack>
                        <Typography
                            color={theme.palette.primary.textTitleColor}
                            variant={isSmallScreen ? "caption" : "titleSmall"}
                        >
                            {t(
                                "views.home.components.tax_submission_generic_error_kpi_card.help_desk_disclaimer",
                            )}
                        </Typography>
                    </Stack>
                </LoadingWrapper>
            </CardContent>
        </Card>
    );
};
